/**
 * Detects what part of an image user is looking at
 * Uses cursor position, zoom level, and hover time
 */

export class FeatureDetector {
    constructor(imageElement) {
        this.imageElement = imageElement;
        this.startTime = Date.now();
        this.interactions = [];
        this.zoomLevel = 1.0;
        this.cursorX = 0;
        this.cursorY = 0;
        this.currentFeature = this.detectFeatureFromPosition(0.5, 0.5);
    }

    /**
     * Map cursor position to property features
     * Divides image into regions representing different areas
     */
    detectFeatureFromPosition(relativeX, relativeY) {
        // Map image positions to features
        // relativeX and relativeY are 0-1 (0=left/top, 1=right/bottom)

        if (relativeY < 0.2) {
            // Top area = Exterior/Entrance/Roof
            if (relativeX < 0.5) return 'entrance';
            return 'view';
        }

        if (relativeY < 0.4) {
            // Upper middle = Balcony/Windows/Facade
            if (relativeX < 0.3) return 'balcony';
            if (relativeX > 0.7) return 'balcony';
            return 'view';
        }

        if (relativeY < 0.6) {
            // Middle = Main living spaces
            if (relativeX < 0.4) return 'bedroom';
            if (relativeX > 0.6) return 'living_room';
            return 'living_room';
        }

        if (relativeY < 0.8) {
            // Lower middle = Yard/Garden/Patio
            if (relativeX < 0.5) return 'garden';
            return 'patio';
        }

        // Bottom area = Pool/Ground features
        if (relativeX < 0.5) return 'yard';
        return 'pool';
    }

    /**
     * Track cursor movement on property image
     */
    trackCursorMovement(event) {
        const rect = this.imageElement.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;

        this.cursorX = x;
        this.cursorY = y;

        // Calculate relative position (0-1)
        const relativeX = x / rect.width;
        const relativeY = y / rect.height;

        // Detect which feature user is looking at
        const detectedFeature = this.detectFeatureFromPosition(relativeX, relativeY);

        if (detectedFeature !== this.currentFeature) {
            // Feature changed - log interaction
            this.logFeatureInteraction('hover');
            this.currentFeature = detectedFeature;
        }
    }

    /**
     * Track zoom/pinch events
     */
    trackZoom(zoomLevel) {
        if (zoomLevel !== this.zoomLevel) {
            this.zoomLevel = zoomLevel;
            this.logFeatureInteraction('zoom');
        }
    }

    /**
     * Track when user likes a property
     */
    trackLike() {
        this.logFeatureInteraction('like');
    }

    /**
     * Log a feature interaction
     */
    logFeatureInteraction(interactionType) {
        const timeSpent = Math.round((Date.now() - this.startTime) / 1000);

        this.interactions.push({
            feature: this.currentFeature,
            type: interactionType,
            zoom_level: this.zoomLevel,
            cursor_x: this.cursorX,
            cursor_y: this.cursorY,
            time_spent: timeSpent,
        });

        this.startTime = Date.now(); // Reset timer
    }

    /**
     * Get all accumulated interactions
     */
    getInteractions() {
        return this.interactions;
    }

    /**
     * Submit tracked data to backend
     */
    async submitToBackend(propertyId, apiClient) {
        if (this.interactions.length === 0) return;

        try {
            // Get the most significant interaction
            const mainInteraction = this.getMostSignificantInteraction();

            const response = await apiClient.post('/interactions/track-interaction/', {
                property_id: propertyId,
                feature_focused: mainInteraction.feature,
                interaction_type: mainInteraction.type,
                zoom_level: mainInteraction.zoom_level,
                cursor_x: mainInteraction.cursor_x,
                cursor_y: mainInteraction.cursor_y,
                time_spent_seconds: mainInteraction.time_spent,
            });

            console.log('Interaction tracked:', response.data);
            return response.data;
        } catch (error) {
            console.error('Failed to track interaction:', error);
        }
    }

    /**
     * Get the interaction user spent most time on
     */
    getMostSignificantInteraction() {
        if (this.interactions.length === 0) {
            return { feature: 'overall', type: 'view', zoom_level: 1.0, cursor_x: 0, cursor_y: 0, time_spent: 0 };
        }

        return this.interactions.reduce((max, current) =>
            current.time_spent > max.time_spent ? current : max
        );
    }
}